import type React from "react";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { Options, Styles } from "react-joyride";

/**
 * Centralized, mode-aware Joyride visual config for ALL guided tours.
 *
 * Every `*Tour.tsx` previously inlined a byte-identical `options` object (only
 * `zIndex` varied: 1300 default / 2100 for FarChat-popup tours / 2300 for the
 * Homepage tour) plus a `styles` object that, in "hint/paused" mode, hid the
 * leftover spotlight cutout (`{ spotlight: { display: "none" } }`). This helper
 * produces both so the dark-mode treatment lives in exactly one place.
 *
 * DARK-MODE-ONLY visual changes (light mode is intentionally byte-unchanged):
 *  1. Gray "fog" overlay instead of joyride's default black dim — a light,
 *     warm-gray semi-transparent wash so the surroundings recede/lighten
 *     rather than darken. Set via `options.overlayColor`.
 *  2. A glowing accent ring traced around the spotlighted target via
 *     `styles.spotlight`. Joyride spreads `styles.spotlight` as props onto an
 *     SVG `<path fill="none">`, so the ring must be drawn with SVG `stroke*`
 *     attributes + a CSS `drop-shadow` filter for the glow (border / outline /
 *     boxShadow do NOT apply to an SVG path).
 *
 * LIGHT MODE: no `overlayColor` override (keep joyride's black dim) and no
 * spotlight ring. When the tour is running normally the entire `styles` prop is
 * `undefined` — byte-identical to the historical inline behavior (the tour
 * tests assert `styles` is `undefined` in this state).
 *
 * HINT/PAUSED MODE (`hideSpotlight: true`): the spotlight cutout is hidden in
 * BOTH modes (`{ spotlight: { display: "none" } }`). `display: "none"` also
 * suppresses the dark-mode ring since the ring IS the spotlight path.
 */

/** Default tour z-index (most tours). */
export const DEFAULT_TOUR_ZINDEX = 1300;

export interface TourStyleOptions {
  /** Per-tour Joyride z-index. Defaults to {@link DEFAULT_TOUR_ZINDEX}. */
  zIndex?: number;
  /**
   * When true (hint / paused mode), hide the spotlight cutout entirely in both
   * light and dark mode. This also hides the dark-mode ring.
   */
  hideSpotlight?: boolean;
}

export interface TourJoyrideConfig {
  /** Passed to `<Joyride options={...} />`. */
  options: Partial<Options>;
  /**
   * Passed to `<Joyride styles={...} />`. `undefined` in light mode while
   * running normally (byte-identical to the historical inline behavior).
   */
  styles: Partial<Styles> | undefined;
}

/**
 * Build the mode-aware Joyride `options` + `styles` for a tour.
 *
 * @param theme MUI theme (read for mode, primary, text, and common white tokens).
 * @param opts  Per-tour z-index and hint-mode flag.
 */
export function getTourJoyride(
  theme: Theme,
  opts?: TourStyleOptions
): TourJoyrideConfig {
  const isDark = theme.palette.mode === "dark";
  const zIndex = opts?.zIndex ?? DEFAULT_TOUR_ZINDEX;
  const hideSpotlight = opts?.hideSpotlight ?? false;

  // `options` is identical across all tours except `zIndex` and (dark mode
  // only) the fog `overlayColor`.
  const options: Partial<Options> = {
    primaryColor: theme.palette.primary.main,
    zIndex,
    overlayClickAction: false,
    // The X button must end the tour (Joyride's default 'close' merely advances
    // to the next step). 'skip' fires STATUS.SKIPPED → handled as an explicit
    // exit by each tour.
    closeButtonAction: "skip",
    // Joyride's built-in Esc handling only supports 'close' | 'next' | 'replay'
    // | false — none of which exit the tour. Disable it; each tour implements
    // Esc-to-exit in its own keydown listener.
    dismissKeyAction: false,
    buttons: ["back", "close", "primary"],
    skipScroll: true,
    // Top-level guard against stray beacons even when a step omits the per-step
    // flag.
    skipBeacon: true,
    loaderDelay: 0,
    targetWaitTimeout: 0,
    // Dark mode: replace the black dim with a soft gray fog.
    ...(isDark ? { overlayColor: alpha(theme.palette.text.primary, 0.34) } : {}),
  };

  // Hint/paused mode: hide the spotlight cutout (and therefore the ring) in
  // both modes. Mirrors the historical inline `{ spotlight: { display: "none" } }`.
  if (hideSpotlight) {
    return {
      options,
      styles: {
        spotlight: {
          display: "none",
        } as React.SVGAttributes<SVGPathElement>,
      },
    };
  }

  // Dark mode (running): draw a glowing white ring around the spotlight. The
  // ring color uses common.white for both the stroke and the drop-shadow glow.
  if (isDark) {
    const ringColor = theme.palette.common.white;
    return {
      options,
      styles: {
        spotlight: {
          stroke: ringColor,
          strokeWidth: 2,
          strokeOpacity: 0.95,
          style: {
            filter: `drop-shadow(0 0 6px ${ringColor}) drop-shadow(0 0 14px ${ringColor})`,
          },
        } as React.SVGAttributes<SVGPathElement>,
      },
    };
  }

  // Light mode (running): byte-unchanged — no overlay override, no ring, and
  // the whole `styles` prop is undefined.
  return { options, styles: undefined };
}
